import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Drawer } from './Drawer'
import { Badge } from './Badge'
import { Btn } from './Btn'
import { useLiveMatchSheet } from './useLiveMatchSheet'
import { fireHaptic } from './settings/SettingsActions'
import { isLiveMatchEnabled } from '../lib/liveMatchFlag'
import { matchClient } from '../api/match-client'
import { track } from '../analytics'
import { CodeInputModal } from './room/CodeInputModal'
import { MAX_MATCH_PLAYERS, type RoomJoinError, type RoomCreateError } from '@sagwa/types'

// ── View state machine ─────────────────────────────────────────────────────────
//
//   options
//     └─ tap Create → createPending → createRoom() success → createdShowing
//                                  └─ error              → options (inline error)
//     └─ tap Join   → joinModalOpen
//                      └─ Submit → joinRoom() success → navigate + close
//                                └─ error              → joinModalOpen (error prop)
//   createdShowing
//     └─ tap Cancel/Leave → close (clearRoomCreated + close sheet)
//
// Quick action: aria-disabled even under Branch X (deferred to W3/W4).
// Rationale: The C1 diagnostic confirmed Branch X is free of server-state
// contamination and seed drift, but the root cause of SUM_MISMATCH in the
// auto-match path is still unresolved. Keeping Quick disabled avoids
// surfacing a known-broken flow while friend rooms (Create/Join) are safe
// to ship because they bypass the queue entirely.

type View = 'options' | 'createPending' | 'joinModalOpen'

// ── i18n key → display error message ─────────────────────────────────────────

function createErrorKey(error: RoomCreateError['error']): string {
  switch (error) {
    case 'STAMINA_EMPTY':
      return 'liveMatch.room.error.expired' // closest mapping; stamina-specific key TBD in W3
    case 'LIVE_MATCH_DISABLED':
      return 'liveMatch.room.error.disabled'
    case 'RATE_LIMITED':
    case 'INTERNAL':
    default:
      return 'liveMatch.room.error.network'
  }
}

function joinErrorKey(error: RoomJoinError['error']): string {
  switch (error) {
    case 'ROOM_NOT_FOUND':
      return 'liveMatch.room.error.notFound'
    case 'INVALID_CODE':
      return 'liveMatch.room.error.invalidCode'
    case 'LIVE_MATCH_DISABLED':
      return 'liveMatch.room.error.disabled'
    case 'STAMINA_EMPTY':
      return 'liveMatch.room.error.expired'
    case 'RATE_LIMITED':
    case 'NETWORK_ERROR':
    case 'INTERNAL':
    default:
      return 'liveMatch.room.error.network'
  }
}

// ── Disabled tap handler (Branch Y) ──────────────────────────────────────────

function onDisabled(): void {
  fireHaptic('warning')
}

// ── Sheet action row ───────────────────────────────────────────────────────────

interface SheetActionProps {
  icon: string
  title: string
  sub: string
  disabled?: boolean
  comingSoon?: string
  pending?: boolean
  onClick?: () => void
}

function SheetAction({
  icon,
  title,
  sub,
  disabled = false,
  comingSoon,
  pending = false,
  onClick,
}: SheetActionProps): ReactElement {
  const handleClick = disabled ? onDisabled : onClick
  return (
    <div
      className="sheet-action"
      aria-disabled={disabled ? 'true' : undefined}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleClick?.()
      }}
    >
      <div className="sheet-action__icon">{pending ? '…' : icon}</div>
      <div className="sheet-action__text">
        <div className="sheet-action__title">{title}</div>
        <div className="sheet-action__sub">{sub}</div>
      </div>
      {disabled && comingSoon != null && (
        <Badge variant="soon">{comingSoon}</Badge>
      )}
    </div>
  )
}

// ── LiveMatchBody — Branch Y (flag=false) ─────────────────────────────────────

function LiveMatchBodyDisabled({ onClose }: { onClose: () => void }): ReactElement {
  const { t } = useTranslation()
  const comingSoon = t('liveMatch.sheet.comingSoon')

  return (
    <>
      <p className="cap" style={{ textAlign: 'center', margin: '-8px 0 8px' }}>
        {t('liveMatch.sheet.hintComingW2')}
      </p>
      <SheetAction
        icon="⚡"
        title={t('liveMatch.sheet.quick').replace('⚡ ', '')}
        sub={t('liveMatch.sheet.quickSub')}
        disabled
        comingSoon={comingSoon}
      />
      <SheetAction
        icon="🆕"
        title={t('liveMatch.sheet.create').replace('🆕 ', '')}
        sub={t('liveMatch.sheet.createSub')}
        disabled
        comingSoon={comingSoon}
      />
      <SheetAction
        icon="🔢"
        title={t('liveMatch.sheet.join').replace('🔢 ', '')}
        sub={t('liveMatch.sheet.joinSub')}
        disabled
        comingSoon={comingSoon}
      />
      <Btn variant="ghost" block onClick={onClose}>
        {t('common.cancel')}
      </Btn>
    </>
  )
}

// ── LiveMatchBody — Branch X (flag=true) ──────────────────────────────────────

function LiveMatchBodyEnabled({ onClose }: { onClose: () => void }): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const ctx = useLiveMatchSheet()

  const [view, setView] = useState<View>('options')
  const [createError, setCreateError] = useState<string | null>(null)
  const [joinError, setJoinError] = useState<string | null>(null)
  const [joinSubmitting, setJoinSubmitting] = useState(false)
  const [quickError, setQuickError] = useState<string | null>(null)
  const [quickSubmitting, setQuickSubmitting] = useState(false)

  // ── Quick handler ───────────────────────────────────────────────────────────
  //
  // Quick (auto-match) is permanently activated post W3+W4 launch. Gating
  // (VITE_QUICK_ENABLED + isQuickEnabled flag) was retired with the LIVE
  // launch cleanup. Outer `isLiveMatchEnabled()` still keeps the entire
  // Enabled body hidden on rollback.
  async function handleQuick(): Promise<void> {
    setQuickSubmitting(true)
    setQuickError(null)

    const result = await matchClient.enterQueue({})
    if (!result.ok) {
      fireHaptic('error')
      track('quick_failed', { error: result.reason })
      setQuickError(
        result.reason === 'NO_STAMINA'
          ? t('daily.staminaZero')
          : result.reason === 'RATE_LIMITED'
            ? t('daily.rateLimited')
            : t('liveMatch.room.error.network'),
      )
      setQuickSubmitting(false)
      return
    }

    fireHaptic('success')
    track('quick_started', { matchId: result.matchId })
    onClose()
    navigate('/matchmaking')
  }

  // ── Create handler ──────────────────────────────────────────────────────────
  //
  // W2.1 hotfix: previously stopped at `setView('createdShowing')` which only
  // surfaced the code inside the sheet — the creator's ws never opened so
  // their own room.countdown never began. Now we hand off to /matchmaking
  // (same as the joiner) so MatchProvider opens the WS, and pass the code via
  // query string so the Matchmaking screen can render <RoomCodeDisplay /> at
  // the top of the lobby waiting view.

  async function handleCreate(): Promise<void> {
    setView('createPending')
    setCreateError(null)

    // No app-level cap — owner controls start via the explicit Start button
    // (W2.2: auto-start at targetSize removed; W3+W4 stepper UI removed).
    // Pass server MAX so the room effectively has no smaller cap beyond the
    // hard-coded 99-player ceiling in @sagwa/types.
    const result = await matchClient.createRoom({ targetSize: MAX_MATCH_PLAYERS })
    if (!result.ok) {
      fireHaptic('error')
      track('room_create_failed', { error: result.error })
      setCreateError(t(createErrorKey(result.error)))
      setView('options')
      return
    }

    fireHaptic('success')
    track('room_created', { matchId: result.matchId, code: result.code })
    ctx.setRoomCreated({
      matchId: result.matchId,
      code: result.code,
      wsUrl: result.wsUrl,
      // W2.2: the creator is always the owner. roomKind/ownerUserId/
      // targetSize come from the Worker response so Matchmaking can render
      // the owner-only "Start" button on first paint (without waiting for
      // the server lobby frame).
      roomKind: result.roomKind ?? 'custom',
      ownerUserId: result.ownerUserId ?? null,
      targetSize: result.targetSize ?? null,
    })
    onClose()
    // W3+W4 P1-e: creator goes to the dedicated RoomLobby (INSIDE AppShell).
    // Auto-match queue still uses /matchmaking; this route is friend-room only.
    navigate(
      `/match/room?matchId=${encodeURIComponent(result.matchId)}&code=${encodeURIComponent(result.code)}`,
    )
  }

  // ── Join handler ────────────────────────────────────────────────────────────

  async function handleJoinSubmit(code: string): Promise<void> {
    setJoinSubmitting(true)
    setJoinError(null)

    const result = await matchClient.joinRoom(code)
    if (!result.ok) {
      fireHaptic('error')
      track('room_join_failed', { error: result.error })
      setJoinError(t(joinErrorKey(result.error)))
      setJoinSubmitting(false)
      return
    }

    fireHaptic('success')
    track('room_joined', { matchId: result.matchId })
    // W2.2 hotfix (F2): joiner must populate the LiveMatchSheet context so
    // Matchmaking.tsx's effect can call connectToRoom() with the pre-issued
    // wsUrl. Without this, the joiner sat on /matchmaking with no socket
    // open — same symptom as the creator pre-F1.
    ctx.setRoomCreated({
      matchId: result.matchId,
      // Joiner already typed the code in — no banner needed. null signals
      // "don't render RoomCodeDisplay".
      code: null,
      wsUrl: result.wsUrl,
      roomKind: result.roomKind ?? 'custom',
      ownerUserId: result.ownerUserId ?? null,
      targetSize: result.targetSize ?? null,
    })
    onClose()
    // W3+W4 P1-e: joiner goes to RoomLobby too. No `code` query (the joiner
    // typed it; no banner needed).
    navigate(`/match/room?matchId=${encodeURIComponent(result.matchId)}`)
  }

  // ── Render: options (+ joinModalOpen overlay) ───────────────────────────────

  const isCreatePending = view === 'createPending'

  return (
    <>
      <SheetAction
        icon="⚡"
        title={t('liveMatch.sheet.quick').replace('⚡ ', '')}
        sub={t('liveMatch.sheet.quickSub')}
        pending={quickSubmitting}
        onClick={() => { void handleQuick() }}
      />
      <SheetAction
        icon="🆕"
        title={t('liveMatch.sheet.create').replace('🆕 ', '')}
        sub={t('liveMatch.sheet.createSub')}
        pending={isCreatePending}
        onClick={() => { void handleCreate() }}
      />
      <SheetAction
        icon="🔢"
        title={t('liveMatch.sheet.join').replace('🔢 ', '')}
        sub={t('liveMatch.sheet.joinSub')}
        onClick={() => {
          setJoinError(null)
          setView('joinModalOpen')
        }}
      />

      {quickError != null && (
        <p
          role="alert"
          style={{
            color: 'var(--danger)',
            fontSize: 13,
            margin: '4px 0 0',
            textAlign: 'center',
          }}
        >
          {quickError}
        </p>
      )}

      {createError != null && (
        <p
          role="alert"
          style={{
            color: 'var(--danger)',
            fontSize: 13,
            margin: '4px 0 0',
            textAlign: 'center',
          }}
        >
          {createError}
        </p>
      )}

      <Btn variant="ghost" block onClick={onClose}>
        {t('common.cancel')}
      </Btn>

      <CodeInputModal
        open={view === 'joinModalOpen'}
        onClose={() => setView('options')}
        onSubmit={handleJoinSubmit}
        error={joinError}
        submitting={joinSubmitting}
      />
    </>
  )
}

// ── LiveMatchSheet — global singleton mounted in App.tsx ───────────────────────

/**
 * Drawer BODY mounted at app root alongside <SettingsDrawer /> INSIDE the
 * <LiveMatchSheetProvider> (App.tsx). Both this body and consumers (Lobby
 * Live Match CTA) share the same context instance through that provider.
 *
 * Dual-layer flag gating:
 *   - Visual gate (Branch Y, VITE_LIVE_MATCH_ENABLED unset/false):
 *     All options aria-disabled. No API calls can be triggered. This is
 *     the production default — safe for all users.
 *   - API gate (Branch X, VITE_LIVE_MATCH_ENABLED=true):
 *     Create + Join options are wired to matchClient. Quick remains
 *     disabled pending SUM_MISMATCH root-cause resolution (W3/W4).
 *
 * Quick action decision: (a) keep Quick disabled under Branch X.
 * See module-level comment in LiveMatchBodyEnabled for full rationale.
 */
export function LiveMatchSheet(): ReactElement {
  const { t } = useTranslation()
  const ctx = useLiveMatchSheet()
  const enabled = isLiveMatchEnabled()

  return (
    <Drawer open={ctx.isOpen} onClose={ctx.close} title={t('liveMatch.sheet.title')}>
      {enabled ? (
        <LiveMatchBodyEnabled onClose={ctx.close} />
      ) : (
        <LiveMatchBodyDisabled onClose={ctx.close} />
      )}
    </Drawer>
  )
}
